import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, CheckCircle2, AlertTriangle, HelpCircle, Settings } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { getBrandName } from '@/lib/branding';

export const EmailHealthCard = () => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.getEmailStatus().then(setStatus).catch(() => setStatus(null));
  }, []);

  if (!status) return null;

  const providerLabel = status.provider_used === 'custom'
    ? `Own account (${status.service === 'sendgrid' ? 'SendGrid' : 'Resend'})`
    : status.provider_used === 'platform' || status.provider_used === 'env'
      ? `${getBrandName()}-managed`
      : 'Not configured';

  const verifyBadge = !status.configured ? null
    : status.sender_verified === true
      ? <Badge className="text-[9px] bg-emerald-500/15 text-emerald-400 border-emerald-500/20"><CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> Sender verified</Badge>
      : status.sender_verified === false
        ? <Badge className="text-[9px] bg-rose-500/15 text-rose-400 border-rose-500/20"><AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> Sender NOT verified</Badge>
        : <Badge className="text-[9px] bg-muted text-muted-foreground"><HelpCircle className="w-2.5 h-2.5 mr-0.5" /> Verification unknown</Badge>;

  return (
    <div className="glass-card rounded-xl p-4" data-testid="email-health-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${status.configured ? 'bg-blue-500/10 border-blue-500/20 text-blue-400' : 'bg-amber-500/10 border-amber-500/20 text-amber-400'}`}>
            <Mail className="w-4 h-4" />
          </div>
          <div>
            <p className="text-xs font-semibold text-foreground">
              Email delivery: <span data-testid="email-health-provider">{providerLabel}</span>
            </p>
            <p className="text-[10px] text-muted-foreground">
              {status.configured
                ? <>Sending as <span className="font-mono">{status.sender}</span> via {status.service === 'sendgrid' ? 'SendGrid' : 'Resend'}</>
                : 'Emails (alerts, schedules, receipts) are disabled until email is configured.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {verifyBadge}
          <Link to="/settings" className="text-[10px] text-primary hover:underline flex items-center gap-1" data-testid="email-health-settings-link">
            <Settings className="w-3 h-3" /> Configure
          </Link>
        </div>
      </div>
      {status.sender_verified === false && (
        <p className="text-[10px] text-rose-400 mt-2">
          Your provider will silently drop emails from an unverified sender — verify the domain in your {status.service === 'sendgrid' ? 'SendGrid' : 'Resend'} dashboard.
        </p>
      )}
    </div>
  );
};
